import React, { useCallback, useMemo, useState } from "react";
import { Download } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { loadEmployeesAcrossBranches, rowsToCsv } from "../../helpers/payrollReportData";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

const CSV_COLS = [
  { key: "branchLabel", header: "Branch" },
  { key: "employeeId", header: "Employee ID" },
  { key: "name", header: "Name" },
  { key: "phone", header: "Phone" },
  { key: "status", header: "Status" },
  { key: "department", header: "Department" },
  { key: "employmentType", header: "Employment type" },
  { key: "monthlySalary", header: "Monthly salary" },
  { key: "companyId", header: "Company ID" },
  { key: "collection", header: "Firestore collection" },
  { key: "docId", header: "Doc ID" },
];

export default function PayrollEmployeeDirectoryReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadEmployeesAcrossBranches(getStoredUser());
      setRows(data);
      setLoaded(true);
      toast.success(`Loaded ${data.length} employee row(s)`);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  }, []);

  const csvText = useMemo(() => {
    if (!rows.length) return "";
    return rowsToCsv(rows, CSV_COLS);
  }, [rows]);

  const downloadCsv = () => {
    if (!rows.length) {
      toast.error("Load data first");
      return;
    }
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `employee_directory_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Payroll · Reports</p>
            <h1 className="text-2xl font-bold text-slate-900">Employee directory (CSV)</h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              All employee documents from branches you can access. Use Refresh after changes in Employees.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
            <button
              type="button"
              onClick={downloadCsv}
              disabled={!loaded || !rows.length}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              <Download className="h-4 w-4" aria-hidden />
              Download CSV
            </button>
          </div>
        </div>

        <div className="mt-6 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-xs">
            <thead className="bg-slate-800 text-[10px] font-bold uppercase tracking-wide text-slate-100">
              <tr>
                {CSV_COLS.filter((c) => !["collection", "docId"].includes(c.key)).map((c) => (
                  <th key={c.key} className="px-2 py-2">
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loaded ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    Click <strong>Refresh</strong> to load.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    No rows (check branch access or employee collections).
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={`${r.collection}-${r.docId}-${i}`} className="border-t border-slate-100 hover:bg-slate-50/80">
                    <td className="px-2 py-1.5">{r.branchLabel}</td>
                    <td className="px-2 py-1.5 font-mono">{r.employeeId}</td>
                    <td className="px-2 py-1.5">{r.name}</td>
                    <td className="px-2 py-1.5">{r.phone}</td>
                    <td className="px-2 py-1.5">{r.status}</td>
                    <td className="px-2 py-1.5">{r.department}</td>
                    <td className="px-2 py-1.5">{r.employmentType}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{String(r.monthlySalary)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
